import { useState, useCallback } from "react";
import { Save, ArrowLeftRight, Pencil, Zap } from "lucide-react";
import { useQuery, useAction } from "convex/react";
import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import type { WorkspaceId } from "@/packages/types";
import { Button } from "@/components/ui";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  SettingsPageHeader,
  SettingsPanel,
  SettingsField,
  SettingsFieldGroup,
} from "@/components/settings/SettingsSection";
import { ProviderStatusBadge } from "./ProviderStatusBadge";
import { ByomConfigForm, type ByomConfigFormState } from "./ByomConfigForm";
import { ConnectionTestResult } from "./ConnectionTestResult";

interface TestResult {
  success: boolean;
  error?: string;
  latencyMs: number;
}

interface AIProviderSettingsProps {
  workspaceId: WorkspaceId;
  role: string;
}

export function AIProviderSettings({
  workspaceId,
  role,
}: AIProviderSettingsProps) {
  const config = useQuery(api.workspace_ai_provider.get, { workspaceId });
  const saveMutation = useApiMutation(api.workspace_ai_provider.save, {
    name: "save-ai-provider",
    successMessage: "AI provider saved",
  });
  const removeMutation = useApiMutation(api.workspace_ai_provider.remove, {
    name: "remove-ai-provider",
    successMessage: "AI provider removed",
  });
  const testAction = useAction(
    api.workspace_ai_provider_actions.testConnection,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [_formState, setFormState] = useState<ByomConfigFormState | null>(null);

  const isAdmin = role === "owner" || role === "admin";
  const isBuiltIn = !config || config.provider === "builtin_openai";
  const isByom = config?.provider === "byom_openai_compatible";

  // "Test Connection" from the form: saves config first, then tests.
  // The testConnection action reads from DB, so config must be saved first.
  const handleTestConnection = useCallback(
    async (formData: ByomConfigFormState) => {
      setIsTesting(true);
      setTestResult(null);
      try {
        // Save config first so testConnection can read it from DB
        await saveMutation.mutateAsync({
          workspaceId,
          provider: "byom_openai_compatible",
          byomRuntime: formData.runtime,
          byomEndpointUrl: formData.endpointUrl,
          byomModelName: formData.modelName,
          byomVisionModelName: formData.visionModelName || undefined,
          byomApiKey: formData.apiKey || undefined,
        });

        // Now test the saved config
        const result = await testAction({ workspaceId });
        setTestResult(result);
      } catch {
        setTestResult({
          success: false,
          error: "Failed to save or test connection",
          latencyMs: 0,
        });
      } finally {
        setIsTesting(false);
      }
    },
    [saveMutation, testAction, workspaceId],
  );

  // "Save" button: just close edit mode (config already saved during test)
  const handleSave = useCallback(() => {
    setIsEditing(false);
    setTestResult(null);
    setFormState(null);
  }, []);

  const handleSwitchToBuiltIn = useCallback(async () => {
    setIsSwitching(true);
    try {
      await removeMutation.mutateAsync({ workspaceId });
      setShowSwitchConfirm(false);
    } finally {
      setIsSwitching(false);
    }
  }, [removeMutation, workspaceId]);

  const handleRetest = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await testAction({ workspaceId });
      setTestResult(result);
    } catch {
      setTestResult({
        success: false,
        error: "Failed to test connection",
        latencyMs: 0,
      });
    } finally {
      setIsTesting(false);
    }
  }, [testAction, workspaceId]);

  // Loading state
  if (config === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[13px] text-muted">
          Loading AI provider settings...
        </p>
      </div>
    );
  }

  // Non-admin: read-only view
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <SettingsPageHeader
          title="AI Provider"
          description="Your workspace AI provider configuration."
        />
        <SettingsPanel title="Current Provider">
          <div className="py-2">
            <p className="text-[14px] text-secondary">
              Your workspace uses:{" "}
              <span className="font-medium text-primary">
                {isBuiltIn
                  ? "Built-in AI (OpenAI)"
                  : `Custom Model (${config?.byomRuntime ?? "custom"})`}
              </span>
            </p>
            <div className="mt-3">
              <ProviderStatusBadge
                provider={config?.provider ?? "builtin_openai"}
                runtime={config?.byomRuntime}
                lastTestSuccess={config?.lastTestSuccess}
                lastTestedAt={config?.lastTestedAt}
              />
            </div>
          </div>
        </SettingsPanel>
      </div>
    );
  }

  // Admin: editing mode
  if (isEditing) {
    return (
      <div className="space-y-6">
        <SettingsPageHeader
          title="AI Provider"
          description="Configure your custom AI model endpoint."
        />
        <SettingsPanel title="Custom Model Configuration">
          <div className="space-y-5">
            <ByomConfigForm
              initialConfig={
                isByom
                  ? {
                      byomRuntime: config?.byomRuntime,
                      byomEndpointUrl: config?.byomEndpointUrl,
                      byomModelName: config?.byomModelName,
                      byomVisionModelName: config?.byomVisionModelName,
                      byomApiKeyEncrypted: config?.byomApiKeyEncrypted,
                    }
                  : undefined
              }
              onTestResult={setTestResult}
              onChange={setFormState}
              onTestConnection={handleTestConnection}
              testResult={testResult}
              isTesting={isTesting}
            />

            <div className="flex items-center gap-2 border-t border-border pt-4">
              <Button
                variant="primary"
                icon={Save}
                onClick={handleSave}
                disabled={!testResult?.success}
              >
                Save
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setTestResult(null);
                  setFormState(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </SettingsPanel>
      </div>
    );
  }

  // Admin: built-in active (read mode)
  if (isBuiltIn) {
    return (
      <div className="space-y-6">
        <SettingsPageHeader
          title="AI Provider"
          description="Manage which AI model powers your workspace."
        />
        <SettingsPanel title="Current Provider">
          <div className="space-y-4">
            <ProviderStatusBadge provider="builtin_openai" />
            <p className="text-[13px] text-secondary">
              Your workspace uses the built-in OpenAI integration. All AI
              features are powered by OpenAI models.
            </p>
            <div className="pt-2">
              <Button
                variant="secondary"
                icon={ArrowLeftRight}
                onClick={() => setIsEditing(true)}
              >
                Switch to Custom Model
              </Button>
            </div>
          </div>
        </SettingsPanel>
      </div>
    );
  }

  // Admin: BYOM active (read mode)
  return (
    <div className="space-y-6">
      <SettingsPageHeader
        title="AI Provider"
        description="Manage which AI model powers your workspace."
      />
      <SettingsPanel title="Current Provider">
        <div className="space-y-4">
          <ProviderStatusBadge
            provider={config.provider}
            runtime={config.byomRuntime}
            lastTestSuccess={config.lastTestSuccess}
            lastTestedAt={config.lastTestedAt}
          />

          <SettingsFieldGroup columns={2}>
            <SettingsField label="Endpoint URL">
              <p className="text-[14px] text-primary">
                {config.byomEndpointUrl ?? "-"}
              </p>
            </SettingsField>
            <SettingsField label="Model Name">
              <p className="text-[14px] text-primary">
                {config.byomModelName ?? "-"}
              </p>
            </SettingsField>
            {config.byomVisionModelName && (
              <SettingsField label="Vision Model">
                <p className="text-[14px] text-primary">
                  {config.byomVisionModelName}
                </p>
              </SettingsField>
            )}
            <SettingsField label="API Key">
              <p className="text-[14px] text-primary">
                {config.byomApiKeyEncrypted ? "***" : "Not set"}
              </p>
            </SettingsField>
          </SettingsFieldGroup>

          {/* Re-test result display */}
          <ConnectionTestResult result={testResult} isTesting={isTesting} />

          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="secondary"
              icon={Zap}
              onClick={handleRetest}
              loading={isTesting}
            >
              Re-test Connection
            </Button>
            <Button
              variant="secondary"
              icon={Pencil}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
            <Button
              variant="danger"
              icon={ArrowLeftRight}
              onClick={() => setShowSwitchConfirm(true)}
            >
              Switch to Built-in AI
            </Button>
          </div>
        </div>
      </SettingsPanel>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={showSwitchConfirm}
        onClose={() => setShowSwitchConfirm(false)}
      >
        <ConfirmDialog.Title>Switch to Built-in AI?</ConfirmDialog.Title>
        <ConfirmDialog.Body>
          This will remove your custom model configuration and switch back to
          the built-in OpenAI provider. This action cannot be undone.
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleSwitchToBuiltIn}
          onCancel={() => setShowSwitchConfirm(false)}
          confirmLabel="Switch to Built-in"
          variant="danger"
          loading={isSwitching}
        />
      </ConfirmDialog>
    </div>
  );
}
